<template>
    <div>
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-medium text-gray-700 dark:text-gray-200">{{ title }}</h3>
        
        <div class="flex items-center space-x-4">
          <select 
            v-model="selectedView" 
            class="bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded border border-gray-300 dark:border-gray-700 py-1 px-3 text-xs"
          >
            <option value="count">Contagem</option>
            <option value="percentage">Porcentagem</option>
          </select>
        </div>
      </div>
      
      <div class="relative h-64">
        <!-- Loading state -->
        <div v-if="loading" class="absolute inset-0 flex items-center justify-center">
          <svg class="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        
        <!-- Chart content -->
        <div v-else class="w-full h-full">
          <div class="relative w-full h-full flex items-end">
            <!-- Y axis labels -->
            <div class="absolute inset-y-0 left-0 flex flex-col justify-between text-xs text-gray-500 dark:text-gray-400 py-4">
              <span>{{ selectedView === 'percentage' ? '100%' : '300' }}</span>
              <span>{{ selectedView === 'percentage' ? '75%' : '225' }}</span>
              <span>{{ selectedView === 'percentage' ? '50%' : '150' }}</span>
              <span>{{ selectedView === 'percentage' ? '25%' : '75' }}</span>
              <span>0</span>
            </div>
            
            <!-- Chart bars -->
            <div class="ml-8 w-full h-full border-l border-b dark:border-gray-700 relative p-4">
              <!-- Grid lines -->
              <div class="absolute inset-0 grid grid-cols-1 grid-rows-4 ml-8">
                <div class="border-t dark:border-gray-700"></div>
                <div class="border-t dark:border-gray-700"></div>
                <div class="border-t dark:border-gray-700"></div>
                <div class="border-t dark:border-gray-700"></div>
              </div>
              
              <!-- Bar chart -->
              <div class="relative w-full h-full flex items-end justify-between z-10">
                <div 
                  v-for="score in 11" 
                  :key="score - 1"
                  class="flex-1 flex flex-col items-center"
                >
                  <!-- Bar -->
                  <div 
                    :class="getBarColor(score - 1)"
                    :style="`height: ${getBarHeight(score - 1)}%`"
                    class="w-6 rounded-t"
                  ></div>
                  
                  <!-- Label -->
                  <div class="mt-2 text-xs text-gray-600 dark:text-gray-400">{{ score - 1 }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Legend -->
      <div class="flex space-x-4 mt-6 justify-center text-sm">
        <div class="flex items-center">
          <span class="inline-block w-3 h-3 bg-red-500 rounded-full mr-1"></span>
          <span class="text-gray-600 dark:text-gray-300">Detratores (0-6)</span>
        </div>
        <div class="flex items-center">
          <span class="inline-block w-3 h-3 bg-yellow-500 rounded-full mr-1"></span>
          <span class="text-gray-600 dark:text-gray-300">Neutros (7-8)</span>
        </div>
        <div class="flex items-center">
          <span class="inline-block w-3 h-3 bg-green-500 rounded-full mr-1"></span>
          <span class="text-gray-600 dark:text-gray-300">Promotores (9-10)</span>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import { ref, computed, watch, onMounted } from 'vue';
  
  export default {
    name: 'ScoreDistribution',
    props: {
      title: {
        type: String,
        default: 'Distribuição de Notas'
      },
      companyId: {
        type: [String, Number],
        default: null
      }
    },
    setup(props) {
      const loading = ref(true);
      const distributions = ref([]);
      const selectedView = ref('count');
      
      // Simulated data loader
      const loadDistributions = async () => {
        loading.value = true;
        
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 800));
        
        // Mock data - in a real app you'd fetch this from an API
        distributions.value = [
          { score: 0, count: 15, percentage: 1.2 },
          { score: 1, count: 12, percentage: 1.0 },
          { score: 2, count: 18, percentage: 1.4 },
          { score: 3, count: 25, percentage: 2.0 },
          { score: 4, count: 30, percentage: 2.4 },
          { score: 5, count: 40, percentage: 3.2 },
          { score: 6, count: 55, percentage: 4.4 },
          { score: 7, count: 120, percentage: 9.6 },
          { score: 8, count: 160, percentage: 12.8 },
          { score: 9, count: 280, percentage: 22.4 },
          { score: 10, count: 490, percentage: 39.2 }
        ];
        
        loading.value = false;
      };
      
      // Get correct height for bar based on score
      const getBarHeight = (score) => {
        const dist = distributions.value.find(d => d.score === score);
        if (!dist) return 0;
        
        const value = selectedView.value === 'percentage' ? dist.percentage : dist.count;
        const maxValue = selectedView.value === 'percentage' ? 100 : 500;
        
        return (value / maxValue) * 100;
      };
      
      // Get color based on NPS category
      const getBarColor = (score) => {
        if (score <= 6) return 'bg-red-500';
        if (score <= 8) return 'bg-yellow-500';
        return 'bg-green-500';
      };
      
      // Initial load
      onMounted(() => {
        loadDistributions();
      });
      
      // Reload if company ID changes
      watch(() => props.companyId, () => {
        loadDistributions();
      });
      
      return {
        loading,
        selectedView,
        getBarHeight,
        getBarColor
      };
    }
  };
  </script>